// common
export const TOKEN = 'token';
export const REFRESH_TOKEN = 'refresh_token';
export const FIRST_REFRESH_TOKEN = 'first_refresh_token';
export const EXPIRED_REFRESH_TOKEN = 60 * 60 * 1000;
export const VINLAB_LOCALE = 'vinlab-locale';
export const VINLAB_VIEW_MODE = 'vinlab_view_mode';

const {
  REACT_APP_OIDC_ACCESS_TOKEN_URI,
  REACT_APP_OIDC_AUTHORIZATION_URI,
  REACT_APP_OIDC_REDIRECT_URI,
  REACT_APP_OIDC_CLIENT_ID,
 REACT_APP_OIDC_LOGOUT_URI,
  REACT_APP_OIDC_USERINFO_ENDPOINT,
  REACT_APP_OIDC_SCOPE,
  REACT_APP_DASHBOARD_URL_PREFIX,
  REACT_APP_SERVER_BASE_URL,
  REACT_APP_UPLOAD_BASE_URL,
  REACT_APP_MEDICAL_VIEWER_URL,
  REACT_APP_OIDC_AUDIENCE,
} = process.env || {};

// Config to run local for dashboard and viewer
// Default to annotation-viewer (will be replaced with clinical-viewer for Clinical Studies)
export const REDIRECT_VIEWER_URL =
  REACT_APP_MEDICAL_VIEWER_URL || window.origin + '/medical-view/annotation-viewer';

export const BASE_ROUTER_PREFIX = REACT_APP_DASHBOARD_URL_PREFIX || '/dashboard';

const BASE_URL = REACT_APP_SERVER_BASE_URL || '';

// Origin used ONLY for DICOM upload POSTs.  In production this points at a
// dedicated HTTP/1.1 listener (port 8443) so uploads are not throttled by
// HTTP/2's per-stream flow-control window.  Falls back to the same origin
// as BASE_URL for dev/local where HTTP/2 isn't in play.
//
// NOTE: do NOT compare against the literal '__UPLOAD_BASE_URL__' sentinel
// here — run.sh's sed replaces every occurrence of that string in the
// bundle, including any that we use as a guard, which collapses
// `'X' !== 'X'` to `false` and silently falls through to BASE_URL.
export const UPLOAD_BASE_URL = REACT_APP_UPLOAD_BASE_URL || BASE_URL || '';

// ---------------------------------------------------------------------------
// Studies-list tunables. All overridable per-deployment via env vars; defaults
// chosen for typical small/mid hospital corpora. Parsed defensively so a
// malformed env value falls back to the default rather than crashing the page.
// ---------------------------------------------------------------------------
const parsePositiveInt = (raw, fallback) => {
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

// Maximum number of studies pulled from Orthanc per fetch for client-side
// filtering. At >~2k studies/tenant, switch to server-side filtering via
// Orthanc /tools/find rather than raising this number.
export const STUDIES_FETCH_LIMIT = parsePositiveInt(
  process.env.REACT_APP_STUDIES_FETCH_LIMIT,
  15000
);

// Minimum gap (ms) between automatic refetches triggered by tab-visibility
// events. Prevents rapid alt-tab from hammering the API.
export const STUDIES_REFETCH_DEBOUNCE_MS = parsePositiveInt(
  process.env.REACT_APP_STUDIES_REFETCH_DEBOUNCE_MS,
  10000
);

export let CONFIG_SERVER = {
  BASE_URL: BASE_URL,
  LOGIN_CALLBACK_URI: REACT_APP_OIDC_REDIRECT_URI || window.origin,
  CLIENT_ID: REACT_APP_OIDC_CLIENT_ID || '',
  RESPONSE_TYPE: 'code',
  AUDIENCE: REACT_APP_OIDC_AUDIENCE || '',
  SCOPE: REACT_APP_OIDC_SCOPE || 'profile',
  STATE: Math.random().toString(36).substring(2),
  OIDC_ACCESS_TOKEN_URI: REACT_APP_OIDC_ACCESS_TOKEN_URI,
  OIDC_AUTHORIZATION_URI: REACT_APP_OIDC_AUTHORIZATION_URI,
  OIDC_LOGOUT_URI: REACT_APP_OIDC_LOGOUT_URI,
  OIDC_USERINFO_ENDPOINT: REACT_APP_OIDC_USERINFO_ENDPOINT,
  TOKEN_PERMISSION: ['api#all'],
};

// routes
export const routes = {
  LOGOUT: '/logout',
  PROJECTS: '/projects',
  STUDIES: '/studies',
  WORKLIST: '/worklist',
  STUDY_LIST: '/study-list',
  STUDY_LIST_ID: '/study-list/:projectId',
  LABEL_MANAGEMENT: '/label-management',
  ADMIN_DASHBOARD: '/admin-dashboard',
  TELERADIOLOGY: '/teleradiology',
  TELERADIOLOGY_ANALYTICS: '/teleradiology/analytics',
  TELERADIOLOGY_TEAM:      '/teleradiology/team',
};

export const STUDY_STATUS = {
  ALL: 'count',
  ASSIGNED: 'ASSIGNED',
  UNASSIGNED: 'UNASSIGNED',
  COMPLETED: 'COMPLETED',
};

export const TASK_STATUS = {
  ALL: 'count',
  NEW: 'NEW',
  DOING: 'DOING',
  COMPLETED: 'COMPLETED',
};

export const LABEL_TYPE = [
  { text: 'Impression', value: 'IMPRESSION' },
  { text: 'Finding', value: 'FINDING' },
];

export const DRAW_SCOPE = {
  STUDY: 'STUDY',
  SERIES: 'SERIES',
  IMAGE: 'IMAGE',
};

export const LABEL_SCOPE = [
  { text: 'Study', value: DRAW_SCOPE.STUDY, isDisable: 'FINDING' },
  { text: 'Series', value: DRAW_SCOPE.SERIES, isDisable: 'FINDING' },
  { text: 'Image', value: DRAW_SCOPE.IMAGE },
];

export const DRAW_TYPE = {
  TAG: 'TAG',
  BOUNDING_BOX: 'BOUNDING_BOX',
  POLYGON: 'POLYGON',
  MASK: 'MASK',
};

export const ANNOTATION_TYPE = [
  { text: 'Tag', value: DRAW_TYPE.TAG, isDisable: 'FINDING' },
  {
    text: 'Bounding box',
    value: DRAW_TYPE.BOUNDING_BOX,
    isDisable: 'IMPRESSION',
  },
  { text: 'Polygon', value: DRAW_TYPE.POLYGON, isDisable: 'IMPRESSION' },
  { text: 'Mask', value: DRAW_TYPE.MASK, isDisable: 'IMPRESSION' },
];

export const DEFAULT_COLOR_PICKER = [
  '#f44336',
  '#e91e63',
  '#9c27b0',
  '#673ab7',
  '#3f51b5',
  '#2196f3',
  '#03a9f4',
  '#00bcd4',
  '#009688',
  '#4caf50',
  '#8bc34a',
  '#cddc39',
  '#ffeb3b',
  '#ffc107',
  '#ff9800',
  '#ff5722',
  '#795548',
  '#607d8b',
];

export const SESSION_TYPE = {
  STUDY: 'STUDY',
  TASK: 'TASK',
};

export const ROLES = {
  PO: 'PO',
  PO_PARTNER: 'PO_PARTNER',
  LABELER: 'Labeler',
};

export const STUDY_TABS = {
  DATA: 'DATA',
  TASK: 'TASK',
  SETTING: 'SETTING',
  ANNOTATE: 'ANNOTATE',
  REVIEW: 'REVIEW',
};

export const WORKFLOW_PROJECT = {
  SINGLE: 'SINGLE',
  TRIANGLE: 'TRIANGLE',
};

export const USER_ROLES = {
  ANNOTATOR: 'ANNOTATOR',
  REVIEWER: 'REVIEWER',
  PROJECT_OWNER: 'PROJECT_OWNER',
};
